import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { StripeProvider } from './StripeProvider';
import { PaymentForm } from './PaymentForm';
import { CREATE_PAYMENT_INTENT, CONFIRM_PAYMENT } from '@shared/graphql/mutations/payment';
import { useAffiliateTracking } from '../../hooks/useAffiliateTracking';
import { usePromoTracking } from '../../hooks/usePromoTracking';
import { useAuth } from '../../contexts/AuthContext';
import analyticsService from '../../services/analytics';
import googleAnalytics from '../../services/googleAnalytics';

interface PackageInfo {
  id: string;
  name: string;
  billingModel: 'subscription' | 'one_time';
  priceWeekly?: number;
  priceMonthly?: number;
  priceOneTime?: number;
  accessDurationDays?: number;
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPackages: string[];
  billingCycle: 'WEEKLY' | 'MONTHLY';
  totalAmount: number;
  onSuccess: () => void;
  selectedTrackId?: string;
  packages?: PackageInfo[]; // Package details for hybrid billing logic
}

interface CreatePaymentIntentInput {
  packageIds: number[];
  billingCycle: 'WEEKLY' | 'MONTHLY';
  paymentType: 'SUBSCRIPTION' | 'ONE_OFF';
  selectedTrackId?: string;
  affiliateCode?: string;
  discountCode?: string;
}

interface ConfirmPaymentInput {
  paymentIntentId: string;
  packageIds: number[];
  billingCycle: 'WEEKLY' | 'MONTHLY';
  paymentType: 'SUBSCRIPTION' | 'ONE_OFF';
  discountCode?: string;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  selectedPackages,
  billingCycle,
  totalAmount,
  onSuccess,
  selectedTrackId,
  packages = [],
}) => {
  const [checkoutUrl, setCheckoutUrl] = useState<string>('');
  const [paymentIntentId, setPaymentIntentId] = useState<string>('');
  const [step, setStep] = useState<'loading' | 'redirecting' | 'confirming' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [paymentType, setPaymentType] = useState<'SUBSCRIPTION' | 'ONE_OFF'>('SUBSCRIPTION');

  const [createPaymentIntent] = useMutation(CREATE_PAYMENT_INTENT);
  const [confirmPayment] = useMutation(CONFIRM_PAYMENT);
  const { getAffiliateForRegistration } = useAffiliateTracking();
  const { promotion, getUserDiscountCode } = usePromoTracking();
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen && selectedPackages.length > 0 && step === 'loading') {
      initializePayment();
    }
  }, [isOpen, selectedPackages, billingCycle, paymentType, step]);

  // Add a key to force complete recreation of Stripe Elements
  const [elementsKey, setElementsKey] = useState<string>('');

  // Reset modal state when opening
  useEffect(() => {
    if (isOpen) {
      // Reset all state for fresh checkout session
      setCheckoutUrl('');
      setPaymentIntentId('');
      setErrorMessage('');
      setElementsKey(Date.now().toString());
      setStep('loading');
    }
  }, [isOpen]);


  // Get user's assigned discount code (prioritized over campaign promotions)
  const userDiscountCode = getUserDiscountCode();
  
  // For logged-in users: ONLY use their assigned discount code, never fall back to campaigns
  // For anonymous users: allow campaign promotions if no user code exists
  const getDiscountCode = () => {
    console.log('🔍 PaymentModal getDiscountCode() DEBUG:');
    console.log('  - user (logged in):', !!user);
    console.log('  - userDiscountCode:', userDiscountCode);
    console.log('  - promotion?.discountCode:', promotion?.discountCode);
    
    if (user) {
      // Logged-in user: ONLY use their assigned discount code (no campaign fallback)
      console.log('  → Logged-in user: returning userDiscountCode:', userDiscountCode);
      return userDiscountCode;
    } else {
      // Anonymous user: use user code first, then campaign promotion as fallback
      const fallbackCode = userDiscountCode || promotion?.discountCode;
      console.log('  → Anonymous user: returning fallback code:', fallbackCode);
      return fallbackCode;
    }
  };
  
  // Create Stripe Checkout Session and redirect
  const createCheckoutSession = async () => {
    try {
      setStep('loading');
      setErrorMessage('');

      // Track subscription attempt
      try {
        analyticsService.trackEvent('subscription_attempt', {
          pageType: 'tips',
          selectedDate: new Date().toISOString().split('T')[0],
          metadata: {
            packageIds: selectedPackages,
            billingCycle: billingCycle,
            paymentType: paymentType,
            selectedTrackId: selectedTrackId,
            isAuthenticated: !!user,
            userType: user ? 'authenticated' : 'anonymous',
            totalAmount: totalAmount
          }
        });
      } catch (analyticsError) {
        console.error('Analytics error:', analyticsError);
      }

      // Get affiliate code for payment
      const affiliateCode = getAffiliateForRegistration();
      
      // Get discount code based on user authentication status
      const discountCode = getDiscountCode();
      
      console.log('🎯 Payment Modal: User logged in:', !!user);
      console.log('🎯 Payment Modal: User discount code:', userDiscountCode);
      console.log('🎯 Payment Modal: Campaign promotion code:', promotion?.discountCode);
      console.log('🎯 Payment Modal: Final discount code:', discountCode);

      const { data } = await createPaymentIntent({
        variables: {
          input: {
            packageIds: selectedPackages.map(id => parseInt(id, 10)),
            billingCycle: billingCycle,
            paymentType: paymentType,
            selectedTrackId: selectedTrackId,
            affiliateCode: affiliateCode,
            discountCode: discountCode,
          } as CreatePaymentIntentInput,
        },
      });

      if (data?.createPaymentIntent?.checkoutUrl) {
        setCheckoutUrl(data.createPaymentIntent.checkoutUrl);
        setPaymentIntentId(data.createPaymentIntent.paymentIntentId);
        setStep('redirecting');
        
        // Track successful checkout session creation
        try {
          analyticsService.trackEvent('checkout_session_created', {
            pageType: 'tips',
            selectedDate: new Date().toISOString().split('T')[0],
            metadata: {
              packageIds: selectedPackages,
              billingCycle: billingCycle,
              paymentType: paymentType,
              selectedTrackId: selectedTrackId,
              checkoutUrl: data.createPaymentIntent.checkoutUrl,
              totalAmount: totalAmount
            }
          });
        } catch (analyticsError) {
          console.error('Analytics error:', analyticsError);
        }

        googleAnalytics.trackBeginCheckout({
          value: totalAmount,
          currency: 'AUD',
          items: selectedPackages.map(packageId => {
            const pkg = packages.find(p => p.id === packageId);
            return {
              item_id: packageId,
              item_name: pkg?.name,
              quantity: 1
            };
          })
        });

        // Redirect to Stripe Checkout
        window.location.href = data.createPaymentIntent.checkoutUrl;
      } else {
        throw new Error('Failed to create checkout session - no checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to initialize payment');
      setStep('error');
    }
  };

  const initializePayment = createCheckoutSession;

  const handlePaymentSuccess = async (stripePaymentIntentId: string) => {
    try {
      setStep('confirming');

      const { data } = await confirmPayment({
        variables: {
          input: {
            paymentIntentId: stripePaymentIntentId,
            packageIds: selectedPackages.map(id => parseInt(id, 10)),
            billingCycle: billingCycle,
            paymentType: paymentType,
            discountCode: getDiscountCode(),
          } as ConfirmPaymentInput,
        },
      });

      if (data?.confirmPayment?.success) {
        // Track affiliate conversion after successful payment confirmation
        const { trackConversion } = useAffiliateTracking();
        try {
          await trackConversion(stripePaymentIntentId);
          console.log(`✅ Affiliate conversion tracked for payment: ${stripePaymentIntentId}`);
        } catch (conversionError) {
          // Don't fail the payment flow if affiliate tracking fails
          console.warn('⚠️ Affiliate conversion tracking failed:', conversionError);
        }

        setStep('success');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        throw new Error(data?.confirmPayment?.message || 'Payment confirmation failed');
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Payment confirmation failed');
      setStep('error');
    }
  };

  const handlePaymentError = (error: string) => {
    setErrorMessage(error);
    setStep('error');
    // Note: Don't clear clientSecret here - let retry create a fresh one
  };

  const handleClose = () => {
    if (step !== 'confirming' && step !== 'redirecting') {
      // Clean up state before closing
      setCheckoutUrl('');
      setPaymentIntentId('');
      setErrorMessage('');
      setStep('loading');
      setElementsKey(Date.now().toString());
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Complete Your {paymentType === 'SUBSCRIPTION' ? 'Subscription' : 'Purchase'}
          </h2>
          {step !== 'confirming' && step !== 'redirecting' && (
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Order Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Order Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Packages ({selectedPackages.length})</span>
                <span>${totalAmount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Billing Cycle</span>
                <span>{billingCycle.toLowerCase()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Payment Type</span>
                <span>{paymentType === 'SUBSCRIPTION' ? 'Recurring Subscription' : 'One-time Payment'}</span>
              </div>
              {paymentType === 'ONE_OFF' && (
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Access Duration</span>
                  <span>{billingCycle === 'MONTHLY' ? '1 month' : '1 week'}</span>
                </div>
              )}
              {getDiscountCode() && (
                <div className="flex justify-between text-sm text-green-700 bg-green-50 px-2 py-1 rounded">
                  <span>Promo Code: {getDiscountCode()}</span>
                  <span>✓ Applied</span>
                </div>
              )}
              <div className="flex justify-between font-medium text-lg pt-2 border-t border-gray-300">
                <span>Total</span>
                <span>${totalAmount.toFixed(2)}</span>
              </div>
              <div className="text-center text-gray-500 text-xs mt-2">
                All prices include GST
                {getDiscountCode() && (
                  <span className="text-green-600 ml-2">
                    • {userDiscountCode ? 'Your assigned discount' : 'Promotional discount'} will be applied at checkout
                  </span>
                )}
              </div>
            </div>
          </div>


          {/* Payment Steps - Payment type selection removed since it's now automatic */}

          {step === 'loading' && (
            <div className="text-center py-8">
              <div className="animate-spin inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
              <p className="text-gray-600">Initializing payment...</p>
            </div>
          )}

          {step === 'redirecting' && (
            <div className="text-center py-8">
              <div className="animate-spin inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
              <p className="text-gray-600">Redirecting to secure payment...</p>
              <p className="text-sm text-gray-500 mt-2">You will be redirected to Stripe Checkout to complete your payment.</p>
            </div>
          )}

          {step === 'confirming' && (
            <div className="text-center py-8">
              <div className="animate-spin inline-block w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full mb-4"></div>
              <p className="text-gray-600">Confirming payment and creating subscriptions...</p>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Payment Successful!</h3>
              <p className="text-gray-600">Your subscriptions have been activated. Redirecting...</p>
            </div>
          )}

          {step === 'error' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Payment Failed</h3>
              <p className="text-gray-600 mb-4">{errorMessage}</p>
              <button
                onClick={createCheckoutSession}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};